import React, { useState, useEffect, useCallback } from "react";
import Spinner from "react-bootstrap/Spinner";
import { useSelector } from "react-redux";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Card from "react-bootstrap/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import IconButton from "@mui/material/IconButton";
import EmptyState from "components/atoms/EmptyState";
import CreateAuthServiceModal from "../../components/organisms/CreateAuthServiceModal/CreateAuthServiceModal";
import {
    getAuthServicesList,
    postDeleteAuthService,
} from "../../helpers/leads/stepHelpers/sendStep1ToServis";
import SwalAlertComp from "../../components/SwalAlert";
import { ConfirmationModal } from "../../components/organisms/ConfirmationModal";
import ServiceCardSkeleton from "components/Leads/ServiceCardSkeleton";
import { Box } from "@mui/material";

const AuthServices = ({
    triggerCreate = false,
    onCreateTriggered = () => {},
}) => {
    const [authServices, setAuthServices] = useState([]);
    const [loading, setLoading] = useState("loading");
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editAuthServiceDetails, setEditAuthServiceDetails] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(null);

    const language = useSelector((state) => state.languagesState.languages);

    // Listen to triggerCreate from parent
    useEffect(() => {
        if (triggerCreate) {
            setShowCreateModal(true);
            onCreateTriggered();
        }
    }, [triggerCreate, onCreateTriggered]);

    const fetchAuthServices = useCallback(async () => {
        setLoading("loading");
        try {
            const response = await getAuthServicesList();
            if (response.status) {
                setAuthServices(response.data || []);
                setLoading("succeeded");
            } else {
                setLoading("failed");
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message:
                        response.message || "Failed to fetch auth services",
                    icon: "error",
                });
            }
        } catch (error) {
            setLoading("failed");
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: "An error occurred while fetching auth services",
                icon: "error",
            });
        }
    }, [language]);

    useEffect(() => {
        fetchAuthServices();
    }, [fetchAuthServices]);

    const handleCreateAuthService = () => {
        setShowCreateModal(true);
    };

    const handleCloseCreateModal = () => {
        setShowCreateModal(false);
    };

    const handleCreateSuccess = () => {
        setShowCreateModal(false);
        fetchAuthServices();
    };

    const handleEdit = (item, event) => {
        event.stopPropagation();
        setEditAuthServiceDetails(item);
        setShowEditModal(true);
    };

    const handleDelete = (id, name, event) => {
        event.stopPropagation();
        setPendingDelete({ id, name });
        setConfirmOpen(true);
    };

    const handleConfirmDelete = async () => {
        setConfirmOpen(false);
        try {
            const response = await postDeleteAuthService(pendingDelete.id);
            if (response.status) {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.deleted ?? "Deleted!",
                    message:
                        language?.delete_swal_modal?.success_delete ??
                        "Auth Service has been deleted successfully.",
                    icon: "success",
                });
                fetchAuthServices();
            } else {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: response.message || "Failed to delete auth service",
                    icon: "error",
                });
            }
        } catch (error) {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: "An error occurred while deleting the auth service",
                icon: "error",
            });
        }
        setPendingDelete(null);
    };

    const getAuthTypeDisplay = (authHeader) => {
        if (!authHeader || !authHeader.type) return "No Auth";
        return authHeader.type?.toString()?.toUpperCase();
    };

    return (
        <div className="">
            <ConfirmationModal
                open={confirmOpen}
                onClose={() => { setConfirmOpen(false); setPendingDelete(null); }}
                onConfirm={handleConfirmDelete}
                title={language?.delete_swal_modal?.are_you_sure ?? "Are you sure?"}
                message={pendingDelete ? `${language?.delete_swal_modal?.delete_text ?? "Do you want to delete"} "${pendingDelete.name}"?` : "This action cannot be undone."}
                confirmText={language?.delete_swal_modal?.yes_delete ?? "Yes"}
                cancelText={language?.delete_swal_modal?.cancel ?? "Cancel"}
                confirmColor="error"
            />
            {loading === "loading" && <ServiceCardSkeleton count={6} />}

            {loading === "succeeded" && !authServices?.length && (
                <EmptyState
                    icon={<VpnKeyOutlinedIcon />}
                    title={
                        language?.auth_services?.no_auth_services_yet ??
                        "No auth services yet"
                    }
                    description={
                        language?.auth_services?.no_auth_services_yet_desc ??
                        "Create an auth service to reuse authentication settings across your web services."
                    }
                />
            )}

            {loading === "succeeded" && !!authServices?.length && (
                <Grid
                    container
                    rowSpacing={1}
                    columnSpacing={{ xs: 12, sm: 12, lg: 4, md: 6 }}
                    className="trackers-area mt-4"
                >
                    {authServices &&
                        Array.isArray(authServices) &&
                        authServices.map((item, index) => (
                            <Grid
                                key={item.id}
                                size={{
                                    xs: 12,
                                    sm: 12,
                                    md: 6,
                                    lg: 4,
                                }}
                            >
                                <Card
                                    className={`dashboard-tree-source-item lead-item service-item dashboard-tree-source-item-${index}`}
                                    style={{
                                        boxShadow: "none",
                                        border: "1px solid #E7E7E7",
                                        borderRadius: "10px",
                                        marginBottom: "20px",
                                        position: "relative",
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                        cursor: "default",
                                    }}
                                >
                                    <div
                                        style={{
                                            position: "absolute",
                                            top: "10px",
                                            right: "10px",
                                            display: "flex",
                                            gap: "5px",
                                            zIndex: 10,
                                        }}
                                    >
                                        <IconButton
                                            size="small"
                                            onClick={(e) => handleEdit(item, e)}
                                            style={{
                                                backgroundColor: "#f5f5f5",
                                                padding: "5px",
                                            }}
                                        >
                                            <EditOutlinedIcon fontSize="small" />
                                        </IconButton>
                                        <Tooltip
                                            title={
                                                !item.deletable
                                                    ? "This auth service is used in a web service"
                                                    : ""
                                            }
                                        >
                                            <Box>
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) =>
                                                        handleDelete(
                                                            item.id,
                                                            item.name,
                                                            e,
                                                        )
                                                    }
                                                    disabled={!item.deletable}
                                                    style={{
                                                        backgroundColor:
                                                            "#f5f5f5",
                                                        padding: "5px",
                                                    }}
                                                >
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </Tooltip>
                                    </div>

                                    <CardContent
                                        style={{ padding: "20px" }}
                                        className="d-flex items-center justify-start"
                                    >
                                        <Typography className="mr-3">
                                            <div
                                                style={{
                                                    width: "2rem",
                                                    height: "2rem",
                                                }}
                                            >
                                                <img
                                                    src="/assets/images/typeImages/lead/destinations/web-service.png"
                                                    alt="Auth Service"
                                                />
                                            </div>
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: 18,
                                                fontWeight: "700",
                                                fontFamily:
                                                    "Nunito, sans-serif",
                                                color: "black",
                                            }}
                                        >
                                            Auth Service
                                        </Typography>
                                    </CardContent>
                                    <CardContent
                                        style={{
                                            padding: "20px",
                                            paddingTop: "0",
                                        }}
                                        className="d-flex items-center justify-start"
                                    >
                                        <div className="lead-service-info-area">
                                            <span
                                                style={{
                                                    display: "block",
                                                    fontSize: "14px",
                                                    color: "black",
                                                    fontWeight: "500",
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        display: "block",
                                                        fontSize: "12px",
                                                        color: "#ABABAB",
                                                        fontWeight: "400",
                                                    }}
                                                >
                                                    {language?.services?.name ??
                                                        "Name"}
                                                </span>

                                                <span
                                                    style={{
                                                        display: "-webkit-box",
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient:
                                                            "vertical",
                                                        overflow: "hidden",
                                                        textOverflow:
                                                            "ellipsis",
                                                        wordBreak: "break-word",
                                                        cursor: "text",
                                                        userSelect: "text",
                                                    }}
                                                >
                                                    {item?.name}
                                                </span>
                                            </span>
                                            <span
                                                style={{
                                                    display: "block",
                                                    fontSize: "14px",
                                                    color: "black",
                                                    fontWeight: "500",
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        display: "block",
                                                        fontSize: "12px",
                                                        color: "#ABABAB",
                                                        fontWeight: "400",
                                                    }}
                                                >
                                                    {language?.services?.url ??
                                                        "URL"}
                                                </span>
                                                <Tooltip
                                                    title={
                                                        item?.request_url || ""
                                                    }
                                                    arrow
                                                    enterDelay={500}
                                                    leaveDelay={200}
                                                >
                                                    <span
                                                        style={{
                                                            display:
                                                                "-webkit-box",
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient:
                                                                "vertical",
                                                            overflow: "hidden",
                                                            textOverflow:
                                                                "ellipsis",
                                                            wordBreak:
                                                                "break-all",
                                                            cursor: "text",
                                                            userSelect: "text",
                                                        }}
                                                    >
                                                        {item?.request_url}
                                                    </span>
                                                </Tooltip>
                                            </span>
                                            <span
                                                style={{
                                                    display: "block",
                                                    fontSize: "14px",
                                                    color: "black",
                                                    fontWeight: "500",
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        display: "block",
                                                        fontSize: "12px",
                                                        color: "#ABABAB",
                                                        fontWeight: "400",
                                                    }}
                                                >
                                                    {language?.services
                                                        ?.method ?? "Method"}
                                                </span>

                                                <span
                                                    style={{
                                                        display: "-webkit-box",
                                                        WebkitLineClamp: 1,
                                                        WebkitBoxOrient:
                                                            "vertical",
                                                        overflow: "hidden",
                                                        textOverflow:
                                                            "ellipsis",
                                                        wordBreak: "break-word",
                                                        cursor: "text",
                                                        userSelect: "text",
                                                    }}
                                                >
                                                    {item?.request_method}
                                                </span>
                                            </span>
                                            <span
                                                style={{
                                                    display: "block",
                                                    fontSize: "14px",
                                                    color: "black",
                                                    fontWeight: "500",
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        display: "block",
                                                        fontSize: "12px",
                                                        color: "#ABABAB",
                                                        fontWeight: "400",
                                                    }}
                                                >
                                                    {language?.services
                                                        ?.auth_type ??
                                                        "Auth Type"}
                                                </span>

                                                <span
                                                    style={{
                                                        display: "-webkit-box",
                                                        WebkitLineClamp: 1,
                                                        WebkitBoxOrient:
                                                            "vertical",
                                                        overflow: "hidden",
                                                        textOverflow:
                                                            "ellipsis",
                                                        wordBreak: "break-word",
                                                        cursor: "text",
                                                        userSelect: "text",
                                                    }}
                                                >
                                                    {getAuthTypeDisplay(
                                                        item?.auth_headers,
                                                    )}
                                                </span>
                                            </span>
                                        </div>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                </Grid>
            )}

            {showCreateModal && (
                <CreateAuthServiceModal
                    onClose={handleCloseCreateModal}
                    onCreateSuccess={handleCreateSuccess}
                />
            )}

            {showEditModal && editAuthServiceDetails && (
                <CreateAuthServiceModal
                    onClose={() => setShowEditModal(false)}
                    onCreateSuccess={handleCreateSuccess}
                    isEditMode
                    authServiceDetails={editAuthServiceDetails}
                />
            )}
        </div>
    );
};

export default AuthServices;
